import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getProjects,
  getGraph,
  Graph,
  Project,
  Category,
  Subcategory,
  DataLayer,
  ProjectId,
  SubcategoryId,
  ContentFormat,
  DataLayerType,
  SourceType,
  CategoryIdSchema,
  CategoryColor,
  SubcategoryIdSchema,
  ProjectIdSchema,
  DataLayerDataSchema,
  DataLayerPlainSchema,
  DataLayerStorySchema,
  DataLayerPlain,
  deleteProject,
  DataLayerIcon,
} from '../api/api.ts';
import cn from '../utils/cn.ts';
import z from 'zod';
import { ChevronDown, ChevronUp, PencilIcon, Trash2 } from 'lucide-react';
import DescriptionView from '../backend/helpers/DescriptionView.tsx';
import CategoryForm from '@/backend/CategoryForm.tsx';
import SubcategoryForm from '@/backend/SubcategoryForm.tsx';
import ProjectForm from '@/backend/ProjectForm.tsx';
import DataLayerForm from '@/backend/DataLayerForm.tsx';

enum EditingType {
  Category = 'category',
  Subcategory = 'subcategory',
  Project = 'project',
  DataLayer = 'datalayer',
}

type EditingState =
  | null
  | { type: EditingType.Category; category: Category & { id?: string } }
  | {
      type: EditingType.Subcategory;
      subcategory: Subcategory & { id?: string };
      category?: Category;
    }
  | {
      type: EditingType.Project;
      category: Category;
      subcategory: Subcategory;
      project: Project & { id?: string };
    }
  | {
      type: EditingType.DataLayer;
      category: Category;
      subcategory: Subcategory;
      project: Project;
      dataLayer: (DataLayer | DataLayerPlain) & { id?: string };
    };

export default function ProjectIndex() {
  const [projectsFromApi, setProjectsFromApi] = useState<Project[] | null>(
    null
  );
  const [graph, setGraph] = useState<Graph | null>(null);

  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      const [projectsResponse, graphResponse] = await Promise.all([
        getProjects(signal || new AbortController().signal),
        getGraph(signal || new AbortController().signal),
      ]);

      setProjectsFromApi(projectsResponse.projects);
      setGraph(graphResponse);
    } catch (e) {
      if (e === 'component destructor') return; // fail gracefully on signal abort
      console.error(e);
      alert(
        'Es gab ein Problem beim Laden der Daten, bitte versuchen Sie es erneut, indem Sie neu laden (Strg+R / Cmd+R).'
      );
    }
  }, []);

  useEffect(() => {
    const aborter = new AbortController();
    loadData(aborter.signal);

    return () => {
      aborter.abort('component destructor');
    };
  }, [loadData]);

  const [revealedDataLayers, setRevealedDataLayers] = useState<ProjectId[]>([]);
  const [revealedProjects, setRevealedProjects] = useState<ProjectId[]>([]);
  const [revealedSubcategories, setRevealedSubcategories] = useState<
    SubcategoryId[]
  >([]);

  const [editing, setEditing] = useState<EditingState>(null);

  const endEditing = () => {
    setEditing(null);
  };

  const handleFormClose = () => {
    endEditing();
    loadData(); // Refresh data after form submission
  };
  const startEditing = (newState: Exclude<EditingState, null>) => {
    setEditing((oldState) => {
      if (oldState === null) {
        return newState;
      }

      if (confirm('You are already editing an entry?\n\nDiscard changes?')) {
        return newState;
      }

      return oldState;
    });
  };

  const categories = useMemo(() => {
    return (
      (graph &&
        graph.categories.map((cat) => ({
          id: cat.id,
          label: cat.label,
          labelDe: cat.labelDe,
          description: cat.description,
          descriptionDe: cat.descriptionDe,
          color: cat.color,
        }))) ||
      []
    );
  }, [graph]);
  const subcategories = useMemo(() => {
    return graph?.categories.flatMap((cat) =>
      cat.subcategories.map((subcat) => ({
        id: subcat.id,
        label: subcat.label,
        labelDe: subcat.labelDe,
        description: subcat.description,
        descriptionDe: subcat.descriptionDe,
        categoryId: subcat.categoryId,
      }))
    );
  }, [graph]);
  const projects = useMemo(() => {
    if (!projectsFromApi) return [];
    return projectsFromApi.map((proj) => ({
      id: proj.id,
      subcategoryId: proj.subcategoryId,
      shortName: proj.shortName,
      shortNameDe: proj.shortNameDe,
      name: proj.name,
      nameDe: proj.nameDe,
      authors: proj.authors,
      content: proj.content,
      contentDe: proj.contentDe,
      shortContent: proj.shortContent,
      shortContentDe: proj.shortContentDe,
      contentFormat: proj.contentFormat,
      doiLabel: proj.doiLabel,
      doiLink: proj.doiLink,
      relations: proj.relations,
      dataLayers: proj.dataLayers,
    }));
  }, [projectsFromApi]);
  const projectsForSubcategoryId = useCallback(
    (subcategoryId: string) => {
      return (
        (projects &&
          projects.filter((proj) => proj.subcategoryId === subcategoryId)) ||
        []
      );
    },
    [projects]
  );
  const subcategoriesForCategoryId = useCallback(
    (categoryId: string) => {
      return (
        (subcategories &&
          subcategories.filter((subcat) => subcat.categoryId === categoryId)) ||
        []
      );
    },
    [subcategories]
  );

  const CategoryUpdateSchema = z.object({
    id: z.string().optional(),
    label: z.string(),
    description: z.string(),
    descriptionDe: z.string(),
  });
  const onSubmitCategory = (data: z.infer<typeof CategoryUpdateSchema>) => {};

  return (
    <div className={cn('h-[100vh]', 'flex', 'items-stretch')}>
      <div className={cn('flex-1', 'overflow-y-auto', 'p-[24px]')}>
        {!(projects && subcategories && categories) &&
          'Data is being loaded...'}
        {projects && subcategories && categories && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h1>
                <div>Categories</div>
              </h1>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    // Find first category to use for new subcategory
                    const firstCategory = categories[0];

                    if (!firstCategory) {
                      alert('Please create a category first');
                      return;
                    }

                    startEditing({
                      type: EditingType.Subcategory,
                      category: firstCategory,
                      subcategory: {
                        id: '',
                        categoryId: firstCategory.id,
                        label: '',
                        labelDe: null,
                        description: '',
                        descriptionDe: null,
                      },
                    });
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  + New Subcategory
                </button>
                <button
                  onClick={() => {
                    // Find first category and subcategory to use for new project
                    const firstCategory = categories[0];
                    const firstSubcategory = subcategoriesForCategoryId(
                      firstCategory?.id
                    )[0];

                    if (!firstCategory || !firstSubcategory) {
                      alert('Please create a category and subcategory first');
                      return;
                    }

                    startEditing({
                      type: EditingType.Project,
                      category: firstCategory,
                      subcategory: firstSubcategory,
                      project: {
                        id: '',
                        subcategoryId: firstSubcategory.id,
                        shortName: '',
                        shortNameDe: null,
                        name: '',
                        nameDe: null,
                        authors: [],
                        content: '',
                        contentDe: null,
                        shortContent: '',
                        shortContentDe: null,
                        contentFormat: ContentFormat.Text,
                        dataLayers: [],
                        relations: {},
                        doiLabel: '',
                        doiLink: '',
                      },
                    });
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                >
                  + New Project
                </button>
              </div>
            </div>
            <ul>
              {categories.map((cat) => (
                <li
                  key={cat.id}
                  className={cn(
                    'border-l-[4px]',
                    'border-t-[4px]',
                    'border-blue-200',
                    'pl-[16px]',
                    'pt-[16px]'
                  )}
                >
                  <div
                    className={cn(
                      'font-bold',
                      'flex',
                      'items-center',
                      'gap-x-[12px]'
                    )}
                  >
                    <div>
                      🇺🇸&nbsp;{cat.label} / 🇩🇪&nbsp;
                      {cat.labelDe || (
                        <span className={cn('italic', 'text-blue-500')}>
                          -empty-
                        </span>
                      )}
                    </div>
                    <PencilIcon
                      width={20}
                      height={20}
                      onClick={() => {
                        startEditing({
                          type: EditingType.Category,
                          category: cat,
                        });
                      }}
                      className={cn(
                        'cursor-pointer',
                        'hover:text-orange-500',
                        'active:text-current'
                      )}
                    />
                  </div>
                  <div>
                    🇺🇸&nbsp;
                    {cat.description || (
                      <span className={cn('italic', 'text-blue-500')}>
                        -empty-
                      </span>
                    )}
                  </div>
                  <div>
                    🇩🇪&nbsp;
                    {cat.descriptionDe || (
                      <span className={cn('italic', 'text-blue-500')}>
                        -empty-
                      </span>
                    )}
                  </div>
                  <h3 className={cn('mt-[24px]')}>Subcategories</h3>
                  <ul>
                    {subcategoriesForCategoryId(cat.id).map((subcat) => (
                      <li
                        key={subcat.id}
                        className={cn(
                          'border-l-[4px]',
                          'border-t-[4px]',
                          'border-green-700',
                          'pl-[16px]',
                          'pt-[16px]'
                        )}
                      >
                        <div>
                          <div
                            className={cn(
                              'font-bold',
                              'flex',
                              'items-center',
                              'gap-x-[12px]'
                            )}
                          >
                            <div
                              className={cn(
                                'cursor-pointer',
                                'hover:text-orange-500',
                                'active:text-current',
                                'flex',
                                'items-center',
                                'gap-x-[12px]'
                              )}
                              onClick={() => {
                                setRevealedSubcategories((current) => {
                                  return current.includes(subcat.id)
                                    ? current.filter((id) => id !== subcat.id)
                                    : [...current, subcat.id];
                                });
                              }}
                            >
                              {(revealedSubcategories.includes(subcat.id) && (
                                <ChevronDown width={24} height={24} />
                              )) || <ChevronUp width={24} height={24} />}
                              <div>
                                🇺🇸&nbsp;{subcat.label} / 🇩🇪&nbsp;
                                {subcat.labelDe || (
                                  <span
                                    className={cn('italic', 'text-blue-500')}
                                  >
                                    -empty-
                                  </span>
                                )}
                              </div>
                            </div>
                            <PencilIcon
                              width={20}
                              height={20}
                              onClick={() => {
                                startEditing({
                                  type: EditingType.Subcategory,
                                  category: cat,
                                  subcategory: subcat,
                                });
                              }}
                              className={cn(
                                'cursor-pointer',
                                'hover:text-orange-500',
                                'active:text-current'
                              )}
                            />
                          </div>
                          {revealedSubcategories.includes(subcat.id) && (
                            <>
                              <div>
                                🇺🇸&nbsp;
                                {subcat.description || (
                                  <span
                                    className={cn('italic', 'text-blue-500')}
                                  >
                                    -empty-
                                  </span>
                                )}
                              </div>
                              <div>
                                🇩🇪&nbsp;
                                {subcat.descriptionDe || (
                                  <span
                                    className={cn('italic', 'text-blue-500')}
                                  >
                                    -empty-
                                  </span>
                                )}
                              </div>

                              {/* PROJECT START */}

                              <h3 className={cn('mt-[24px]')}>Projects</h3>

                              <ul>
                                {projectsForSubcategoryId(subcat.id).map(
                                  (proj) => (
                                    <li
                                      key={proj.id}
                                      className={cn(
                                        'border-l-[4px]',
                                        'border-t-[4px]',
                                        'border-red-700',
                                        'pl-[16px]',
                                        'pt-[16px]'
                                      )}
                                    >
                                      <div
                                        className={cn(
                                          'font-bold',
                                          'flex',
                                          'items-center',
                                          'gap-x-[12px]'
                                        )}
                                      >
                                        <div
                                          className={cn(
                                            'cursor-pointer',
                                            'hover:text-orange-500',
                                            'active:text-current',
                                            'flex',
                                            'items-center',
                                            'gap-x-[12px]'
                                          )}
                                          onClick={() => {
                                            setRevealedProjects((current) => {
                                              return current.includes(proj.id)
                                                ? current.filter(
                                                    (id) => id !== proj.id
                                                  )
                                                : [...current, proj.id];
                                            });
                                          }}
                                        >
                                          {(revealedProjects.includes(
                                            proj.id
                                          ) && (
                                            <ChevronDown
                                              width={24}
                                              height={24}
                                            />
                                          )) || (
                                            <ChevronUp width={24} height={24} />
                                          )}
                                          <div>
                                            🇺🇸 &nbsp;{proj.shortName} / 🇩🇪&nbsp;
                                            {proj.shortNameDe || (
                                              <span
                                                className={cn(
                                                  'italic',
                                                  'text-blue-500'
                                                )}
                                              >
                                                -empty-
                                              </span>
                                            )}
                                            <div>{proj.id}</div>
                                          </div>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                          <PencilIcon
                                            width={20}
                                            height={20}
                                            onClick={() => {
                                              startEditing({
                                                type: EditingType.Project,
                                                category: cat,
                                                subcategory: subcat,
                                                project: proj,
                                              });
                                            }}
                                            className={cn(
                                              'cursor-pointer',
                                              'hover:text-orange-500',
                                              'active:text-current'
                                            )}
                                          />
                                          <Trash2
                                            width={20}
                                            height={20}
                                            onClick={async () => {
                                              if (
                                                confirm(
                                                  `Are you sure you want to delete project "${proj.name}"?\n\nThis action cannot be undone.`
                                                )
                                              ) {
                                                try {
                                                  await deleteProject(proj.id);
                                                  loadData(); // Refresh the list
                                                } catch (error) {
                                                  alert(
                                                    `Failed to delete project: ${
                                                      error instanceof Error
                                                        ? error.message
                                                        : 'Unknown error'
                                                    }`
                                                  );
                                                }
                                              }
                                            }}
                                            className={cn(
                                              'cursor-pointer',
                                              'hover:text-red-500',
                                              'active:text-current'
                                            )}
                                          />
                                        </div>
                                      </div>
                                      {revealedProjects.includes(proj.id) && (
                                        <>
                                          <div className={cn('text-[.7em]')}>
                                            <div>
                                              <span className={cn('italic')}>
                                                Category:
                                              </span>{' '}
                                              {cat.label}
                                            </div>
                                            <div>
                                              <span className={cn('italic')}>
                                                Category:
                                              </span>{' '}
                                              {subcat.label}
                                            </div>
                                          </div>
                                          <p
                                            className={cn(
                                              'font-bold',
                                              'mt-[24px]'
                                            )}
                                          >
                                            Full title
                                          </p>
                                          <div>
                                            🇺🇸&nbsp;
                                            {proj.name || (
                                              <span
                                                className={cn(
                                                  'italic',
                                                  'text-blue-500'
                                                )}
                                              >
                                                -empty-
                                              </span>
                                            )}
                                          </div>
                                          <div>
                                            🇩🇪&nbsp;
                                            {proj.nameDe || (
                                              <span
                                                className={cn(
                                                  'italic',
                                                  'text-blue-500'
                                                )}
                                              >
                                                -empty-
                                              </span>
                                            )}
                                          </div>
                                          <p
                                            className={cn(
                                              'font-bold',
                                              'mt-[24px]'
                                            )}
                                          >
                                            Short description
                                          </p>
                                          <DescriptionView
                                            content={proj.shortContent}
                                            format={proj.contentFormat}
                                            lang={'en'}
                                          />
                                          <DescriptionView
                                            content={proj.shortContentDe}
                                            format={proj.contentFormat}
                                            lang={'de'}
                                          />
                                          <p
                                            className={cn(
                                              'font-bold',
                                              'mt-[24px]'
                                            )}
                                          >
                                            Full description
                                          </p>
                                          <DescriptionView
                                            content={proj.content}
                                            format={proj.contentFormat}
                                            lang={'en'}
                                            cutoff={250}
                                          />
                                          <DescriptionView
                                            content={proj.contentDe}
                                            format={proj.contentFormat}
                                            lang={'de'}
                                            cutoff={250}
                                          />
                                          <p
                                            className={cn(
                                              'font-bold',
                                              'mt-[24px]'
                                            )}
                                          >
                                            Authors
                                          </p>
                                          <div className={cn('text-[.7em]')}>
                                            {proj.authors.map((auth, index) => (
                                              <div key={index}>{auth}</div>
                                            ))}
                                            {proj.authors.length === 0 && (
                                              <span
                                                className={cn(
                                                  'italic',
                                                  'text-blue-500'
                                                )}
                                              >
                                                -empty-
                                              </span>
                                            )}
                                          </div>
                                          <p
                                            className={cn(
                                              'font-bold',
                                              'mt-[24px]'
                                            )}
                                          >
                                            DOI Link/Label
                                          </p>
                                          <div className={cn('text-[.7em]')}>
                                            {proj.doiLabel || (
                                              <span
                                                className={cn(
                                                  'italic',
                                                  'text-blue-500'
                                                )}
                                              >
                                                -empty-
                                              </span>
                                            )}
                                          </div>{' '}
                                          <div className={cn('text-[.7em]')}>
                                            {proj.doiLink || (
                                              <span
                                                className={cn(
                                                  'italic',
                                                  'text-blue-500'
                                                )}
                                              >
                                                -empty-
                                              </span>
                                            )}
                                          </div>
                                          {/* DATALAYERS START */}
                                          <h4>
                                            <div>Data Layers</div>
                                          </h4>
                                          <div className="flex start gap-x-[6px] items-center mb-6">
                                            {(!proj.dataLayers.length ||
                                              proj.dataLayers[0].type ===
                                                DataLayerType.Data) && (
                                              <button
                                                onClick={() => {
                                                  // Find first category and subcategory to use for new project
                                                  const firstCategory =
                                                    categories[0];
                                                  const firstSubcategory =
                                                    subcategoriesForCategoryId(
                                                      firstCategory?.id
                                                    )[0];

                                                  if (
                                                    !firstCategory ||
                                                    !firstSubcategory
                                                  ) {
                                                    alert(
                                                      'Please create a category and subcategory first'
                                                    );
                                                    return;
                                                  }

                                                  startEditing({
                                                    type: EditingType.DataLayer,
                                                    category: cat,
                                                    subcategory: subcat,
                                                    project: proj,
                                                    dataLayer: {
                                                      id: '',
                                                      type: DataLayerType.Data,
                                                      shortName: '',
                                                      shortNameDe: '',
                                                      defaultVisibility: true,
                                                      icon: DataLayerIcon.Static,
                                                      parameters: {},
                                                      dataSources: [],
                                                    },
                                                  });
                                                }}
                                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                              >
                                                + New Data-DataLayer
                                              </button>
                                            )}
                                            {proj.dataLayers.length === 0 && (
                                              <button
                                                onClick={() => {
                                                  // Find first category and subcategory to use for new project
                                                  const firstCategory =
                                                    categories[0];
                                                  const firstSubcategory =
                                                    subcategoriesForCategoryId(
                                                      firstCategory?.id
                                                    )[0];

                                                  if (
                                                    !firstCategory ||
                                                    !firstSubcategory
                                                  ) {
                                                    alert(
                                                      'Please create a category and subcategory first'
                                                    );
                                                    return;
                                                  }

                                                  startEditing({
                                                    type: EditingType.DataLayer,
                                                    category: cat,
                                                    subcategory: subcat,
                                                    project: proj,
                                                    dataLayer: {
                                                      id: '',
                                                      type: DataLayerType.Story,
                                                      shortName: '',
                                                      shortNameDe: '',
                                                      defaultVisibility: true,
                                                      icon: DataLayerIcon.Story,
                                                      layers: [],
                                                    },
                                                  });
                                                }}
                                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                              >
                                                + New Story-DataLayer
                                              </button>
                                            )}
                                          </div>
                                          <ul>
                                            {proj.dataLayers.map((lay) => (
                                              <li
                                                key={lay.id}
                                                className={cn(
                                                  'border-l-[4px]',
                                                  'border-t-[4px]',
                                                  'border-orange-300',
                                                  'pl-[16px]',
                                                  'pt-[16px]'
                                                )}
                                              >
                                                <div
                                                  className={cn(
                                                    'font-bold',
                                                    'flex',
                                                    'items-center',
                                                    'gap-x-[12px]'
                                                  )}
                                                >
                                                  <div
                                                    className={cn(
                                                      'cursor-pointer',
                                                      'hover:text-orange-500',
                                                      'active:text-current',
                                                      'flex',
                                                      'items-center',
                                                      'gap-x-[12px]'
                                                    )}
                                                    onClick={() => {
                                                      setRevealedDataLayers(
                                                        (current) => {
                                                          return current.includes(
                                                            lay.id
                                                          )
                                                            ? current.filter(
                                                                (id) =>
                                                                  id !== lay.id
                                                              )
                                                            : [
                                                                ...current,
                                                                lay.id,
                                                              ];
                                                        }
                                                      );
                                                    }}
                                                  >
                                                    {(revealedDataLayers.includes(
                                                      lay.id
                                                    ) && (
                                                      <ChevronDown
                                                        width={24}
                                                        height={24}
                                                      />
                                                    )) || (
                                                      <ChevronUp
                                                        width={24}
                                                        height={24}
                                                      />
                                                    )}
                                                    <div>
                                                      {lay.id} (
                                                      {
                                                        {
                                                          [DataLayerType.Data]:
                                                            'geodata layer',
                                                          [DataLayerType.Story]:
                                                            'story layer',
                                                          [DataLayerType.PlainProject]:
                                                            'marker-group layer',
                                                        }[lay.type]
                                                      }
                                                      )
                                                    </div>
                                                  </div>
                                                  <PencilIcon
                                                    width={20}
                                                    height={20}
                                                    onClick={() => {
                                                      startEditing({
                                                        type: EditingType.DataLayer,
                                                        category: cat,
                                                        subcategory: subcat,
                                                        project: proj,
                                                        dataLayer: lay,
                                                      });
                                                    }}
                                                    className={cn(
                                                      'cursor-pointer',
                                                      'hover:text-orange-500',
                                                      'active:text-current'
                                                    )}
                                                  />
                                                </div>
                                                {revealedDataLayers.includes(
                                                  lay.id
                                                ) && (
                                                  <>
                                                    {lay.type ===
                                                      DataLayerType.Story && (
                                                      <div>
                                                        {lay.layers.map(
                                                          (
                                                            storypoint,
                                                            index
                                                          ) => (
                                                            <div
                                                              key={index}
                                                              className={cn(
                                                                'border-l-[4px]',
                                                                'border-t-[4px]',
                                                                'border-white',
                                                                'pl-[16px]',
                                                                'pt-[16px]'
                                                              )}
                                                            >
                                                              <p
                                                                className={cn(
                                                                  'font-bold',
                                                                  'mt-[24px]'
                                                                )}
                                                              >
                                                                Markers
                                                              </p>
                                                              <div
                                                                className={cn(
                                                                  'flex',
                                                                  'flex-col'
                                                                )}
                                                              >
                                                                {storypoint.markers.map(
                                                                  (
                                                                    marker,
                                                                    index
                                                                  ) => (
                                                                    <div
                                                                      key={
                                                                        index
                                                                      }
                                                                    >
                                                                      [
                                                                      {
                                                                        marker[0]
                                                                      }
                                                                      ,{' '}
                                                                      {
                                                                        marker[1]
                                                                      }
                                                                      ]
                                                                    </div>
                                                                  )
                                                                )}
                                                              </div>

                                                              <p
                                                                className={cn(
                                                                  'font-bold',
                                                                  'mt-[24px]'
                                                                )}
                                                              >
                                                                Short
                                                                description
                                                              </p>
                                                              <DescriptionView
                                                                content={
                                                                  storypoint.content
                                                                }
                                                                format={
                                                                  proj.contentFormat
                                                                }
                                                                lang={'en'}
                                                              />
                                                              <DescriptionView
                                                                content={
                                                                  storypoint.contentDe
                                                                }
                                                                format={
                                                                  proj.contentFormat
                                                                }
                                                                lang={'de'}
                                                              />
                                                            </div>
                                                          )
                                                        )}
                                                      </div>
                                                    )}
                                                    {lay.type ===
                                                      DataLayerType.PlainProject && (
                                                      <div>
                                                        <p
                                                          className={cn(
                                                            'font-bold',
                                                            'mt-[24px]'
                                                          )}
                                                        >
                                                          Markers
                                                        </p>
                                                        <div
                                                          className={cn(
                                                            'flex',
                                                            'flex-col'
                                                          )}
                                                        >
                                                          {lay.markers.map(
                                                            (marker, index) => (
                                                              <div key={index}>
                                                                [{marker[0]},{' '}
                                                                {marker[1]}]
                                                              </div>
                                                            )
                                                          )}
                                                        </div>
                                                      </div>
                                                    )}
                                                    {lay.type ===
                                                      DataLayerType.Data && (
                                                      <>
                                                        <p
                                                          className={cn(
                                                            'font-bold',
                                                            'mt-[24px]'
                                                          )}
                                                        >
                                                          Datasources
                                                        </p>

                                                        {lay.dataSources.map(
                                                          (
                                                            source,
                                                            sourceIndex
                                                          ) => (
                                                            <div
                                                              key={sourceIndex}
                                                            >
                                                              <div>
                                                                Datasource{' '}
                                                                {sourceIndex +
                                                                  1}
                                                              </div>

                                                              <div>
                                                                <span
                                                                  className={cn(
                                                                    'italic'
                                                                  )}
                                                                >
                                                                  Type:{' '}
                                                                </span>
                                                                {
                                                                  {
                                                                    [SourceType.TilesTMS]:
                                                                      'TMS tile layer',
                                                                    [SourceType.GeoRaster]:
                                                                      'Georaster',
                                                                    [SourceType.GeoJson]:
                                                                      'GeoJson',
                                                                    [SourceType.None]:
                                                                      'none',
                                                                  }[
                                                                    source
                                                                      .dataSource
                                                                      .type
                                                                  ]
                                                                }
                                                              </div>
                                                              {source.dataSource
                                                                .type ===
                                                                SourceType.TilesTMS && (
                                                                <>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      URL:{' '}
                                                                    </span>
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .url
                                                                    }
                                                                  </div>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      minZoom:{' '}
                                                                    </span>
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .minZoom
                                                                    }
                                                                  </div>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      maxZoom:{' '}
                                                                    </span>
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .maxZoom
                                                                    }
                                                                  </div>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      minValue:{' '}
                                                                    </span>
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .minValue
                                                                    }
                                                                  </div>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      maxValue:{' '}
                                                                    </span>
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .maxValue
                                                                    }
                                                                  </div>
                                                                </>
                                                              )}
                                                              {source.dataSource
                                                                .type ===
                                                                SourceType.GeoJson && (
                                                                <>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      URL:{' '}
                                                                    </span>
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .url
                                                                    }
                                                                  </div>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      Bounds:{' '}
                                                                    </span>
                                                                    [
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .bounds[0][0]
                                                                    }
                                                                    ,{' '}
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .bounds[0][1]
                                                                    }
                                                                    ] to [
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .bounds[1][0]
                                                                    }
                                                                    ,{' '}
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .bounds[1][1]
                                                                    }
                                                                    ]
                                                                  </div>
                                                                  {source
                                                                    .dataSource
                                                                    .propertyForSelectorColoring && (
                                                                    <div>
                                                                      <span
                                                                        className={cn(
                                                                          'italic'
                                                                        )}
                                                                      >
                                                                        Property
                                                                        for
                                                                        coloring:{' '}
                                                                      </span>
                                                                      {
                                                                        source
                                                                          .dataSource
                                                                          .propertyForSelectorColoring
                                                                      }
                                                                    </div>
                                                                  )}
                                                                  {source
                                                                    .dataSource
                                                                    .parameterForPropertySelectorColoring && (
                                                                    <div>
                                                                      <span
                                                                        className={cn(
                                                                          'italic'
                                                                        )}
                                                                      >
                                                                        Parameter
                                                                        for
                                                                        coloring:{' '}
                                                                      </span>
                                                                      {
                                                                        source
                                                                          .dataSource
                                                                          .parameterForPropertySelectorColoring
                                                                      }
                                                                    </div>
                                                                  )}
                                                                </>
                                                              )}
                                                              {source.dataSource
                                                                .type ===
                                                                SourceType.GeoRaster && (
                                                                <>
                                                                  <div>
                                                                    <span
                                                                      className={cn(
                                                                        'italic'
                                                                      )}
                                                                    >
                                                                      URL:{' '}
                                                                    </span>
                                                                    {
                                                                      source
                                                                        .dataSource
                                                                        .url
                                                                    }
                                                                  </div>
                                                                </>
                                                              )}
                                                              {source.dataSource
                                                                .type ===
                                                                SourceType.None && (
                                                                <div className="text-gray-500 italic">
                                                                  No data source
                                                                  configured
                                                                </div>
                                                              )}
                                                              {source
                                                                .dataSourceTriggers
                                                                .length > 0 && (
                                                                <div>
                                                                  <span
                                                                    className={cn(
                                                                      'italic'
                                                                    )}
                                                                  >
                                                                    Triggers:{' '}
                                                                  </span>
                                                                  {source.dataSourceTriggers.join(
                                                                    ', '
                                                                  )}
                                                                </div>
                                                              )}
                                                            </div>
                                                          )
                                                        )}
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                          {/* DATALAYERS END */}
                                        </>
                                      )}
                                    </li>
                                  )
                                )}
                              </ul>

                              {/* PROJECT END */}
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div className={cn('flex-1', 'overflow-y-auto', 'p-[24px]')}>
        {/* FORMS */}
        {editing && editing.type === EditingType.Category && (
          <CategoryForm
            key={editing.category.id}
            category={editing.category}
            onClose={handleFormClose}
          />
        )}
        {editing && editing.type === EditingType.Subcategory && (
          <SubcategoryForm
            key={editing.subcategory.id}
            subcategory={editing.subcategory}
            onClose={handleFormClose}
            isEditing={!!editing.subcategory.id}
          />
        )}
        {editing && editing.type === EditingType.Project && (
          <ProjectForm
            key={editing.project.id}
            project={editing.project}
            onClose={handleFormClose}
            isEditing={!!editing.project.id}
          />
        )}
        {editing &&
          editing.type === EditingType.DataLayer &&
          (() => {
            // Get the current project data to ensure contentFormat is up-to-date
            const currentProject = projects.find(
              (p) => p.id === editing.project.id
            );
            const currentContentFormat =
              currentProject?.contentFormat || editing.project.contentFormat;

            return (
              <DataLayerForm
                key={editing.dataLayer.id}
                dataLayer={editing.dataLayer}
                projectId={editing.project.id}
                onClose={handleFormClose}
                contentFormat={currentContentFormat}
                isEditing={!!editing.dataLayer.id}
              />
            );
          })()}
        {!editing && (
          <div className="text-gray-500 text-center mt-8">
            <p>Select an item to edit by clicking the pencil icon</p>
          </div>
        )}
      </div>
    </div>
  );
}
